import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/axios";
import { mockApiService, shouldUseMockData } from "../mockData/mockLocketData";

/**
 * Whether the current JWT belongs to the owner of this QR sticker.
 * Uses /qr/me (qrId on account) so owner UI does not depend on the
 * legacy /qr/:id/profile route alone.
 *
 * `checking` stays true until ownership is known, so callers can avoid
 * acting on the initial `isOwner: false`.
 */
export function useQrOwnership(qrId) {
  const { token, user, fetchUser } = useAuth();
  const [state, setState] = useState({ isOwner: false, checking: Boolean(token && qrId) });

  useEffect(() => {
    if (!token || !qrId) {
      setState({ isOwner: false, checking: false });
      return undefined;
    }

    let cancelled = false;
    const finish = (isOwner) => {
      if (!cancelled) setState({ isOwner, checking: false });
    };

    const check = async () => {
      try {
        if (shouldUseMockData()) {
          await mockApiService.getCustomerProfile(qrId, token);
          finish(true);
          return;
        }

        let account = user;
        if (!account?.qrId) {
          account = await fetchUser();
        }

        const ownsQr =
          account?.qrId &&
          account.qrId.toUpperCase() === String(qrId).toUpperCase();

        if (ownsQr) {
          finish(true);
          return;
        }

        // /qr/me already says this account owns a different QR (or none).
        // Do not hit /qr/:id/profile — that ownership check returns 403 by design
        // and was noisy in Cloud Logging during bystander scans while logged in.
        if (account && Object.prototype.hasOwnProperty.call(account, "qrId")) {
          finish(false);
          return;
        }

        await api.get(`/qr/${qrId}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        finish(true);
      } catch {
        finish(false);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [token, qrId, user, fetchUser]);

  return state;
}

/** True when the current JWT belongs to the owner of this QR sticker. */
export function useIsQrOwner(qrId) {
  return useQrOwnership(qrId).isOwner;
}
